/**
 * Browser-compatible Excel import utilities.
 *
 * These functions accept a browser File object, read it into an ArrayBuffer,
 * and parse it using the xlsx library. The parsed data is returned as typed
 * domain objects ready to be batch-written to Firestore via the service layer.
 *
 * This module is the backoffice equivalent of the (now-deleted) seed-forestry.ts
 * and seed-pricing.ts scripts.
 */
import * as XLSX from 'xlsx';
import type {
  ForestType,
  OperationType,
  InterventionType,
  ForestryOperation,
} from '../../../../2bforest-common/types/forestry';
import type {
  ProductPricing,
  ProductType,
  PricingCategory1,
  PricingCategory2,
  PricingCategory3,
  PricingCategory4,
} from '../../../../2bforest-common/types/market';
import { slugify, generateSearchKeywordsFromFields } from '../../../../2bforest-common/utils';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Read a browser File into an ArrayBuffer. */
async function readFileBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target!.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// ---------------------------------------------------------------------------
// Forestry types
// ---------------------------------------------------------------------------

export interface ForestryImportData {
  forestTypes: ForestType[];
  operationTypes: (OperationType & { linkedForestTypeIds?: string[] })[];
  interventionTypes: (InterventionType & {
    linkedOperationTypeIds?: string[];
    linkedForestTypeIds?: string[];
  })[];
  operations: ForestryOperation[];
}

interface ParsedForestryHierarchy {
  forestTypes: Set<string>;
  operationTypes: Set<string>;
  interventionTypes: Set<string>;
  operationTypeAssociations: Array<{ operationType: string; forestType: string }>;
  interventionTypeAssociations: Array<{ interventionType: string; operationType: string }>;
  operations: Array<{
    forestType: string;
    operationType: string;
    interventionType: string;
    description: string;
  }>;
}

function parseOpcoesSheet(worksheet: XLSX.WorkSheet): ParsedForestryHierarchy {
  const rows = XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, { defval: '' });

  if (!rows.length) {
    throw new Error('No data rows found in "Opções" sheet');
  }

  const headers = Object.keys(rows[0]);
  console.log('[forestry import] "Opções" headers:', headers);

  const forestTypes = new Set<string>();
  const operationTypes = new Set<string>();
  const interventionTypes = new Set<string>();
  const operationTypeAssociations: Array<{ operationType: string; forestType: string }> = [];
  const interventionTypeAssociations: Array<{ interventionType: string; operationType: string }> =
    [];
  const operations: Array<{
    forestType: string;
    operationType: string;
    interventionType: string;
    description: string;
  }> = [];

  const uniqueForestTypes = new Set<string>(
    rows.map(r => r['Tipo_de_floresta']?.trim()).filter(v => v && v.length > 0)
  );

  for (const forestType of uniqueForestTypes) {
    forestTypes.add(forestType);
  }

  for (const forestType of uniqueForestTypes) {
    const opPattern = new RegExp(`^Operações_${forestType.replace(/\s+/g, '_')}`, 'i');
    const opColHeader = headers.find(h => opPattern.test(h.trim().replace(/\s+/g, '_')));
    if (!opColHeader) continue;

    const operationValues = Array.from(
      new Set(rows.map(r => r[opColHeader]?.trim()).filter(v => v && v.length > 0))
    );

    for (const operation of operationValues) {
      operationTypes.add(operation);
      if (
        !operationTypeAssociations.find(
          a => a.operationType === operation && a.forestType === forestType
        )
      ) {
        operationTypeAssociations.push({ operationType: operation, forestType });
      }

      const tiposPattern = new RegExp(
        `^(?:TiposIntervenção|Tipo_de_Intervenções)_${forestType.replace(/\s+/g, '_')}_${operation.replace(/\s+/g, '_')}`,
        'i'
      );
      const tipoColHeader = headers.find(h => tiposPattern.test(h.trim().replace(/\s+/g, '_')));
      if (!tipoColHeader) continue;

      const interventionValues = Array.from(
        new Set(rows.map(r => r[tipoColHeader]?.trim()).filter(v => v && v.length > 0))
      );

      for (const interventionType of interventionValues) {
        interventionTypes.add(interventionType);
        if (
          !interventionTypeAssociations.find(
            a => a.interventionType === interventionType && a.operationType === operation
          )
        ) {
          interventionTypeAssociations.push({ interventionType, operationType: operation });
        }

        const intervencaoPattern = new RegExp(
          `^Intervenções_${forestType.replace(/\s+/g, '_')}_${operation.replace(/\s+/g, '_')}_${interventionType.replace(/\s+/g, '_')}`,
          'i'
        );
        const intervColHeader = headers.find(h =>
          intervencaoPattern.test(h.trim().replace(/\s+/g, '_'))
        );
        if (!intervColHeader) continue;

        const interventionDescriptions = Array.from(
          new Set(rows.map(r => r[intervColHeader]?.trim()).filter(v => v && v.length > 0))
        );
        for (const description of interventionDescriptions) {
          operations.push({ forestType, operationType: operation, interventionType, description });
        }
      }
    }
  }

  return {
    forestTypes,
    operationTypes,
    interventionTypes,
    operationTypeAssociations,
    interventionTypeAssociations,
    operations,
  };
}

interface PricingEntry {
  minValue: number;
  avgValue: number;
  maxValue: number;
  fixedPrice: number | null;
  pricePerHour: number | null;
  unit: string;
  minValueConditions: string;
  maxValueConditions: string;
  source: string;
}

function parseCustosSheet(worksheet: XLSX.WorkSheet): Map<string, PricingEntry> {
  const rows = XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, { defval: '' });
  const map = new Map<string, PricingEntry>();

  if (rows.length > 0) {
    console.log('[forestry import] "Custos e Proveitos" headers:', Object.keys(rows[0]));
  }

  for (const row of rows) {
    const description = row['Descrição']?.trim();
    if (!description) continue;

    const parseNum = (v: string | undefined) =>
      v ? parseFloat(String(v).replace(',', '.')) : null;
    const safeNum = (v: number | null) => (v !== null && !isNaN(v) ? v : 0);

    const pricePerHour = parseNum(row['Preço p/hora']);
    const minValue = parseNum(row['Valor Mínimo']);
    const avgValue = parseNum(row['Valor Médio']);
    const maxValue = parseNum(row['Valor Máximo']);
    const fixedPrice = parseNum(row['Preço único '] ?? row['Preço único']);

    map.set(description, {
      minValue: safeNum(minValue),
      avgValue: safeNum(avgValue),
      maxValue: safeNum(maxValue),
      fixedPrice: fixedPrice !== null && !isNaN(fixedPrice) ? fixedPrice : null,
      pricePerHour: pricePerHour !== null && !isNaN(pricePerHour) ? pricePerHour : null,
      unit: row['Unidade']?.trim() || 'ha',
      minValueConditions: row['Condicionantes valor mínimo']?.trim() || '',
      maxValueConditions: row['Condicionantes valor máximo']?.trim() || '',
      source: row['Tabela']?.trim() || '',
    });
  }
  return map;
}

function getPricingForDescription(
  description: string,
  pricingMap: Map<string, PricingEntry>
): PricingEntry {
  if (pricingMap.has(description)) return pricingMap.get(description)!;
  for (const [key, value] of pricingMap) {
    if (
      description.toLowerCase().includes(key.toLowerCase()) ||
      key.toLowerCase().includes(description.toLowerCase())
    ) {
      return value;
    }
  }
  return {
    minValue: 0,
    avgValue: 0,
    maxValue: 0,
    fixedPrice: null,
    pricePerHour: null,
    unit: '',
    minValueConditions: '',
    maxValueConditions: '',
    source: '',
  };
}

/**
 * Parse a Forestry Excel file (Valores CAOF_FSIM_Mobile.xlsx) from a browser File object.
 * Returns all typed entities ready for Firestore import.
 */
export async function parseForestryOperationsFile(file: File): Promise<ForestryImportData> {
  const buffer = await readFileBuffer(file);
  const wb = XLSX.read(buffer, { type: 'array' });

  console.log('[forestry import] Sheet names found:', wb.SheetNames);

  const opcoesSheet = wb.Sheets['Opções'];
  if (!opcoesSheet) throw new Error('Sheet "Opções" not found in the uploaded file.');

  const custosSheet = wb.Sheets['Custos e Proveitos'];
  const pricingMap = custosSheet ? parseCustosSheet(custosSheet) : new Map<string, PricingEntry>();

  const hierarchy = parseOpcoesSheet(opcoesSheet);
  console.log('[forestry import] Parsed hierarchy:', {
    forestTypes: Array.from(hierarchy.forestTypes),
    operationTypes: hierarchy.operationTypes.size,
    interventionTypes: hierarchy.interventionTypes.size,
    operations: hierarchy.operations.length,
  });

  // --- Forest types ---
  const forestTypeIdMap = new Map<string, string>();
  const forestTypesList: ForestType[] = Array.from(hierarchy.forestTypes)
    .sort()
    .map((name, idx) => {
      const id = slugify(name);
      forestTypeIdMap.set(name, id);
      return { id, name: name.replace(/_/g, ' '), order: idx + 1 };
    });

  // --- Operation types (with linked forest type IDs) ---
  const operationTypeIdMap = new Map<string, string>();
  const operationTypeToForestTypes = new Map<string, Set<string>>();
  for (const { operationType, forestType } of hierarchy.operationTypeAssociations) {
    if (!operationTypeToForestTypes.has(operationType))
      operationTypeToForestTypes.set(operationType, new Set());
    operationTypeToForestTypes.get(operationType)!.add(forestType);
  }

  const operationTypesList = Array.from(hierarchy.operationTypes)
    .sort()
    .map((name, idx) => {
      const id = slugify(name);
      operationTypeIdMap.set(name, id);
      const ftNames = operationTypeToForestTypes.get(name) || new Set();
      return {
        id,
        name: name.replace(/_/g, ' '),
        order: idx + 1,
        linkedForestTypeIds: Array.from(ftNames).map(ft => slugify(ft)),
      };
    });

  // --- Intervention types (with linked operation type IDs + forest type IDs) ---
  const interventionTypeIdMap = new Map<string, string>();
  const interventionTypeToOperationTypes = new Map<string, Set<string>>();
  for (const { interventionType, operationType } of hierarchy.interventionTypeAssociations) {
    if (!interventionTypeToOperationTypes.has(interventionType))
      interventionTypeToOperationTypes.set(interventionType, new Set());
    interventionTypeToOperationTypes.get(interventionType)!.add(operationType);
  }

  const interventionTypesList = Array.from(hierarchy.interventionTypes)
    .sort()
    .map((name, idx) => {
      const id = slugify(name);
      interventionTypeIdMap.set(name, id);
      const opNames = interventionTypeToOperationTypes.get(name) || new Set();
      const linkedOperationTypeIds = Array.from(opNames).map(op => slugify(op));
      const forestTypeIdsSet = new Set<string>();
      for (const opName of opNames) {
        for (const ftName of operationTypeToForestTypes.get(opName) || []) {
          forestTypeIdsSet.add(slugify(ftName));
        }
      }
      return {
        id,
        name: name.replace(/_/g, ' '),
        order: idx + 1,
        linkedOperationTypeIds,
        linkedForestTypeIds: Array.from(forestTypeIdsSet),
      };
    });

  // --- Interventions: build per-description groupings ---
  const interventionTypeIdToOperationTypeIds = new Map<string, Set<string>>();
  for (const { interventionType, operationType } of hierarchy.interventionTypeAssociations) {
    const itId = interventionTypeIdMap.get(interventionType) ?? slugify(interventionType);
    const otId = operationTypeIdMap.get(operationType) ?? slugify(operationType);
    if (!interventionTypeIdToOperationTypeIds.has(itId))
      interventionTypeIdToOperationTypeIds.set(itId, new Set());
    interventionTypeIdToOperationTypeIds.get(itId)!.add(otId);
  }

  const operationTypeIdToForestTypeIds = new Map<string, Set<string>>();
  for (const { operationType, forestType } of hierarchy.operationTypeAssociations) {
    const otId = operationTypeIdMap.get(operationType) ?? slugify(operationType);
    const ftId = forestTypeIdMap.get(forestType) ?? slugify(forestType);
    if (!operationTypeIdToForestTypeIds.has(otId))
      operationTypeIdToForestTypeIds.set(otId, new Set());
    operationTypeIdToForestTypeIds.get(otId)!.add(ftId);
  }

  const operationsByDescription = new Map<string, Set<string>>();
  for (const op of hierarchy.operations) {
    const itId = interventionTypeIdMap.get(op.interventionType);
    if (!itId) continue;
    if (!operationsByDescription.has(op.description))
      operationsByDescription.set(op.description, new Set());
    operationsByDescription.get(op.description)!.add(itId);
  }

  const operations: ForestryOperation[] = [];
  for (const [description, interventionTypeIds] of operationsByDescription) {
    const id = slugify(description || 'operation');
    const pricing = getPricingForDescription(description, pricingMap);

    const linkedOperationTypeIdsSet = new Set<string>();
    const linkedForestTypeIdsSet = new Set<string>();
    for (const itId of interventionTypeIds) {
      for (const otId of interventionTypeIdToOperationTypeIds.get(itId) || []) {
        linkedOperationTypeIdsSet.add(otId);
        for (const ftId of operationTypeIdToForestTypeIds.get(otId) || []) {
          linkedForestTypeIdsSet.add(ftId);
        }
      }
    }

    operations.push({
      id,
      description,
      linkedInterventionTypeIds: Array.from(interventionTypeIds),
      linkedOperationTypeIds: Array.from(linkedOperationTypeIdsSet),
      linkedForestTypeIds: Array.from(linkedForestTypeIdsSet),
      minValue: pricing.minValue,
      avgValue: pricing.avgValue,
      maxValue: pricing.maxValue,
      pricePerHour: pricing.pricePerHour,
      fixedPrice: pricing.fixedPrice,
      unit: pricing.unit,
      totalUnits: null,
      avgTotalCost: null,
      minValueConditions: pricing.minValueConditions,
      maxValueConditions: pricing.maxValueConditions,
      source: pricing.source || null,
      isActive: true,
      searchKeywords: generateSearchKeywordsFromFields([
        description,
        pricing.minValueConditions,
        pricing.maxValueConditions,
      ]),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  return {
    forestTypes: forestTypesList,
    operationTypes: operationTypesList,
    interventionTypes: interventionTypesList,
    operations,
  };
}

// ---------------------------------------------------------------------------
// Product pricing types
// ---------------------------------------------------------------------------

export interface PricingImportData {
  productTypes: ProductType[];
  category1Items: PricingCategory1[];
  category2Items: PricingCategory2[];
  category3Items: PricingCategory3[];
  category4Items: PricingCategory4[];
  pricingItems: ProductPricing[];
}

function normalizePricingHeader(h: string): string {
  const s = h.trim().toLowerCase();
  if (s.includes('designação')) return 'designation';
  if (s.includes('preço') || s.includes('preco')) return 'price';
  if (s.includes('iva')) return 'vat';
  if (s.includes('nota')) return 'notes';
  if (s.includes('legislação') || s.includes('legislacao')) return 'applicableLaw';
  // Check in reverse order to avoid substring matches (IV before III before II before I)
  if (s.includes('categoria') && s.includes('iv')) return 'category4';
  if (s.includes('categoria') && s.includes('iii')) return 'category3';
  if (s.includes('categoria') && s.includes('ii')) return 'category2';
  if (s.includes('categoria') && s.includes('i')) return 'category1';
  return s;
}

/**
 * Parse a Product Pricing Excel file (TabelaPrecos*.xlsx) from a browser File object.
 * Returns all typed entities ready for Firestore import.
 *
 * The Excel file has one sheet per product type; each sheet may contain rows with
 * up to four category levels (Categoria I – IV), a designation, price, VAT, notes,
 * and applicable law.
 */
export async function parseProductPricingFile(file: File): Promise<PricingImportData> {
  const buffer = await readFileBuffer(file);
  const wb = XLSX.read(buffer, { type: 'array' });

  const productTypeMap = new Map<
    string,
    { name: string; order: number; category1Ids: Set<string> }
  >();
  const category1Map = new Map<
    string,
    { name: string; order: number; category2Ids: Set<string> }
  >();
  const category2Map = new Map<
    string,
    { name: string; order: number; category3Ids: Set<string> }
  >();
  const category3Map = new Map<
    string,
    { name: string; order: number; category4Ids: Set<string> }
  >();
  const category4Map = new Map<string, { name: string; order: number }>();
  const allItems: ProductPricing[] = [];

  let productTypeOrder = 0;

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true }) as unknown[][];
    if (!rows.length) continue;

    const headers = (rows[0] as (string | null)[]).map(h =>
      normalizePricingHeader(String(h || ''))
    );
    const idx = (key: string) => headers.indexOf(key);

    const productTypeId = slugify(sheetName);
    if (!productTypeMap.has(productTypeId)) {
      productTypeMap.set(productTypeId, {
        name: sheetName.trim(),
        order: productTypeOrder++,
        category1Ids: new Set(),
      });
    }

    const isValidCategory = (val: string) => val && val !== '-';

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r] as unknown[];
      if (!row || (row as unknown[]).every(v => v == null || String(v).trim() === '')) continue;

      const designation =
        idx('designation') >= 0 ? String(row[idx('designation')] ?? '').trim() : '';
      if (!designation) continue;

      const cat1Val = idx('category1') >= 0 ? String(row[idx('category1')] ?? '').trim() : '';
      const cat2Val = idx('category2') >= 0 ? String(row[idx('category2')] ?? '').trim() : '';
      const cat3Val = idx('category3') >= 0 ? String(row[idx('category3')] ?? '').trim() : '';
      const cat4Val = idx('category4') >= 0 ? String(row[idx('category4')] ?? '').trim() : '';

      let cat1Id: string | undefined;
      let cat2Id: string | undefined;
      let cat3Id: string | undefined;
      let cat4Id: string | undefined;

      if (isValidCategory(cat1Val)) {
        cat1Id = slugify(cat1Val);
        productTypeMap.get(productTypeId)!.category1Ids.add(cat1Id);
        if (!category1Map.has(cat1Id))
          category1Map.set(cat1Id, {
            name: cat1Val,
            order: category1Map.size,
            category2Ids: new Set(),
          });

        if (isValidCategory(cat2Val)) {
          cat2Id = slugify(cat2Val);
          category1Map.get(cat1Id)!.category2Ids.add(cat2Id);
          if (!category2Map.has(cat2Id))
            category2Map.set(cat2Id, {
              name: cat2Val,
              order: category2Map.size,
              category3Ids: new Set(),
            });

          if (isValidCategory(cat3Val)) {
            cat3Id = slugify(cat3Val);
            category2Map.get(cat2Id)!.category3Ids.add(cat3Id);
            if (!category3Map.has(cat3Id))
              category3Map.set(cat3Id, {
                name: cat3Val,
                order: category3Map.size,
                category4Ids: new Set(),
              });

            if (isValidCategory(cat4Val)) {
              cat4Id = slugify(cat4Val);
              category3Map.get(cat3Id)!.category4Ids.add(cat4Id);
              if (!category4Map.has(cat4Id))
                category4Map.set(cat4Id, { name: cat4Val, order: category4Map.size });
            }
          }
        }
      }

      const notesVal = idx('notes') >= 0 ? String(row[idx('notes')] ?? '').trim() : '';
      const lawVal =
        idx('applicableLaw') >= 0 ? String(row[idx('applicableLaw')] ?? '').trim() : '';

      const idParts = [productTypeId, designation];
      if (cat1Id) idParts.push(cat1Id);
      if (cat2Id) idParts.push(cat2Id);
      if (cat3Id) idParts.push(cat3Id);
      if (cat4Id) idParts.push(cat4Id);

      const itemId = slugify(idParts.join('_'));

      const item: ProductPricing = {
        id: itemId,
        productType: sheetName.trim(),
        productTypeId,
        designation,
        price: idx('price') >= 0 ? Number(row[idx('price')]) || 0 : 0,
        vat: idx('vat') >= 0 ? Number(row[idx('vat')]) || 0 : 0,
        searchKeywords: generateSearchKeywordsFromFields([designation, notesVal, lawVal]),
      };

      if (cat1Val && isValidCategory(cat1Val)) {
        item.category1 = cat1Val;
        item.category1Id = cat1Id;
      }
      if (cat2Val && isValidCategory(cat2Val)) {
        item.category2 = cat2Val;
        item.category2Id = cat2Id;
      }
      if (cat3Val && isValidCategory(cat3Val)) {
        item.category3 = cat3Val;
        item.category3Id = cat3Id;
      }
      if (cat4Val && isValidCategory(cat4Val)) {
        item.category4 = cat4Val;
        item.category4Id = cat4Id;
      }
      if (notesVal) item.notes = notesVal;
      if (lawVal) item.applicableLaw = lawVal;

      allItems.push(item);
    }
  }

  const productTypes: ProductType[] = Array.from(productTypeMap.entries()).map(([id, d]) => ({
    id,
    name: d.name,
    order: d.order,
    linkedCategory1Ids: Array.from(d.category1Ids),
  }));
  const category1Items: PricingCategory1[] = Array.from(category1Map.entries()).map(([id, d]) => ({
    id,
    name: d.name,
    order: d.order,
    linkedCategory2Ids: Array.from(d.category2Ids),
  }));
  const category2Items: PricingCategory2[] = Array.from(category2Map.entries()).map(([id, d]) => ({
    id,
    name: d.name,
    order: d.order,
    linkedCategory3Ids: Array.from(d.category3Ids),
  }));
  const category3Items: PricingCategory3[] = Array.from(category3Map.entries()).map(([id, d]) => ({
    id,
    name: d.name,
    order: d.order,
    linkedCategory4Ids: Array.from(d.category4Ids),
  }));
  const category4Items: PricingCategory4[] = Array.from(category4Map.entries()).map(([id, d]) => ({
    id,
    name: d.name,
    order: d.order,
  }));

  return {
    productTypes,
    category1Items,
    category2Items,
    category3Items,
    category4Items,
    pricingItems: allItems,
  };
}
